import { useCallback, useEffect, useState } from 'react';
import themeService, { type ThemePreset, presetSlug } from '../../../services/theme.service';
import type { ThemeDoc } from '../../../theme/types';
import { BUILT_IN_PRESETS } from './presets';

export type PresetKind = 'built-in' | 'personal' | 'org';

export interface PresetEntry {
  slug: string;
  name: string;
  kind: PresetKind;
  doc: ThemeDoc;
  /** Only the user's own presets and org presets can be removed. */
  deletable: boolean;
}

/**
 * The preset library: built-ins, this user's saved themes, and the org's.
 *
 * Personal presets live in one user_settings blob and org presets in one
 * default_table_settings row each, so saving a personal preset is a read-modify-
 * write of the whole list. That is why `savePersonal` always sends the list it
 * just computed rather than relying on state having settled.
 */
export function useCustomizePresets(scope: 'user' | 'org') {
  const [personal, setPersonal] = useState<ThemePreset[]>([]);
  const [org, setOrg] = useState<ThemePreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [p, o] = await Promise.all([
      themeService.getPersonalPresets(),
      themeService.listOrgPresets(),
    ]);
    setPersonal(p);
    setOrg(o);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, o] = await Promise.all([
          themeService.getPersonalPresets(),
          themeService.listOrgPresets(),
        ]);
        if (cancelled) return;
        setPersonal(p);
        setOrg(o);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const entries: PresetEntry[] = [
    ...BUILT_IN_PRESETS.map((b) => ({
      slug: b.slug, name: b.name, kind: 'built-in' as const, doc: b.doc, deletable: false,
    })),
    ...personal.map((p) => ({
      slug: p.slug,
      name: p.doc.name ?? p.slug,
      kind: 'personal' as const,
      doc: p.doc,
      deletable: true,
    })),
    ...org.map((p) => ({
      slug: p.slug,
      name: p.doc.name ?? p.slug,
      kind: 'org' as const,
      doc: p.doc,
      // Only offer to delete an org preset from the org scope, where the user is
      // already working with the permission that governs it.
      deletable: scope === 'org',
    })),
  ];

  /**
   * Save the current draft as a named preset in the active scope.
   *
   * Re-saving an existing name replaces it, because the slug is derived from the
   * name - which is what people expect from "save as" when they reuse a label,
   * and avoids a library filling up with near-duplicates.
   */
  const save = useCallback(async (name: string, doc: ThemeDoc) => {
    setError(null);
    const slug = presetSlug(name);
    if (!slug) {
      setError('Give the preset a name with at least one letter or number.');
      return false;
    }
    try {
      if (scope === 'org') {
        await themeService.saveOrgPreset(name, doc);
      } else {
        const next = [
          ...personal.filter((p) => p.slug !== slug),
          { slug, doc: { ...doc, name } },
        ];
        await themeService.savePersonalPresets(next);
      }
      await reload();
      return true;
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'Could not save the preset.');
      return false;
    }
  }, [scope, personal, reload]);

  const remove = useCallback(async (entry: PresetEntry) => {
    setError(null);
    try {
      if (entry.kind === 'org') {
        await themeService.deleteOrgPreset(entry.slug);
      } else if (entry.kind === 'personal') {
        await themeService.savePersonalPresets(personal.filter((p) => p.slug !== entry.slug));
      }
      await reload();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'Could not delete the preset.');
    }
  }, [personal, reload]);

  return { entries, isLoading, error, save, remove };
}
